// Date-range presets for the `/discover` informational-events surface. Each
// preset maps to a `{ date_from?, date_to? }` pair of YYYY-MM-DD strings that
// feed the backend list query.
//
// No Vue/Nuxt imports — auto-imported by Nuxt from `app/utils/`. The mapping is
// kept pure/testable by accepting an optional `now` argument (defaulting to the
// current date), so unit tests can pin "today" without touching the clock.

export type DiscoverDatePresetKey = 'anytime' | 'month' | '3mo' | '6mo';

export interface DiscoverDatePreset {
  key: DiscoverDatePresetKey;
  label: string;
}

// Ordered for stable rendering in the date-range selector.
export const DISCOVER_DATE_PRESETS: readonly DiscoverDatePreset[] = [
  { key: 'anytime', label: 'Anytime' },
  { key: 'month', label: 'This month' },
  { key: '3mo', label: 'Next 3 months' },
  { key: '6mo', label: 'Next 6 months' },
] as const;

export const DEFAULT_DISCOVER_DATE_PRESET: DiscoverDatePresetKey = 'anytime';

export interface DiscoverDateRange {
  date_from?: string;
  date_to?: string;
}

// Format a Date as a local YYYY-MM-DD string (no UTC shift).
function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Narrow an arbitrary string to a known preset key, falling back to the default.
export function normalizeDiscoverDatePreset(
  value: string | undefined,
): DiscoverDatePresetKey {
  return DISCOVER_DATE_PRESETS.some((preset) => preset.key === value)
    ? (value as DiscoverDatePresetKey)
    : DEFAULT_DISCOVER_DATE_PRESET;
}

/**
 * Map a date-range preset key to `{ date_from?, date_to? }` (YYYY-MM-DD).
 *
 * Every preset floors `date_from` at today, so the discover feed never surfaces
 * past (concluded) events regardless of the selected range.
 *
 * - `anytime` → today .. (no upper bound) — "any upcoming date".
 * - `month`   → today .. last day of the current month.
 * - `3mo`     → today .. today + 3 months.
 * - `6mo`     → today .. today + 6 months.
 */
export function discoverDateRange(
  key: DiscoverDatePresetKey,
  now: Date = new Date(),
): DiscoverDateRange {
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateFrom = formatLocalDate(from);

  // "Anytime" means "any upcoming date": floor at today, no upper bound.
  if (key === 'anytime') return { date_from: dateFrom };

  let to: Date;
  if (key === 'month') {
    // Day 0 of next month is the last day of the current month.
    to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  } else if (key === '3mo') {
    to = new Date(now.getFullYear(), now.getMonth() + 3, now.getDate());
  } else {
    to = new Date(now.getFullYear(), now.getMonth() + 6, now.getDate());
  }

  return { date_from: dateFrom, date_to: formatLocalDate(to) };
}
